// ============================================================
// select-picker.ts  — セレクトをモーダルの表選択に置き換える
// ============================================================
//
// タブレットではドロップダウンが扱いにくいため、data-select-picker を付けた
// <select> はボタン表示に変え、タップでモーダルを開いて表から選ばせる。
// 複数選択 (multiple) はチェックボックスの表にして「決定」で確定する。
// <select> 自体は残したまま値を書き戻すので、フォーム送信・既存の
// スクリプト (値の設定や change 監視) はそのまま動く。

interface PickerParts {
    dialog: HTMLDialogElement;
    title: HTMLElement;
    filter: HTMLInputElement;
    body: HTMLTableSectionElement;
    head: HTMLTableSectionElement;
    empty: HTMLElement;
    clear: HTMLButtonElement;
    apply: HTMLButtonElement;
}

declare global {
    interface Window {
        __ncSelectPicker?: PickerParts;
    }
}

function build(): PickerParts {
    const dialog = document.createElement('dialog');
    dialog.className = 'border border-dark rounded p-0 nc-select-picker';
    dialog.setAttribute('aria-label', '一覧から選択');

    const header = document.createElement('div');
    header.className = 'modal-header bg-dark text-white';
    const title = document.createElement('h5');
    title.className = 'modal-title';
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'btn-close btn-close-white';
    close.setAttribute('aria-label', '閉じる');
    close.addEventListener('click', () => dialog.close());
    header.append(title, close);

    const body = document.createElement('div');
    body.className = 'modal-body';
    const filter = document.createElement('input');
    filter.type = 'search';
    filter.className = 'form-control mb-3';
    filter.placeholder = '名称で絞り込み';
    filter.setAttribute('aria-label', '名称で絞り込み');
    const wrapper = document.createElement('div');
    wrapper.className = 'table-responsive nc-select-picker-list';
    const table = document.createElement('table');
    table.className = 'table table-sm table-bordered border-dark table-hover align-middle mb-0';
    const head = document.createElement('thead');
    head.className = 'table-dark text-center align-middle';
    const rows = document.createElement('tbody');
    table.append(head, rows);
    wrapper.append(table);
    const empty = document.createElement('p');
    empty.className = 'mb-0 mt-2';
    empty.textContent = '該当する候補はありません。';
    empty.hidden = true;
    body.append(filter, wrapper, empty);

    const footer = document.createElement('div');
    footer.className = 'modal-footer';
    const button = (label: string, className: string) => {
        const element = document.createElement('button');
        element.type = 'button';
        element.className = className;
        element.textContent = label;
        return element;
    };
    const cancel = button('キャンセル', 'btn btn-secondary');
    cancel.addEventListener('click', () => dialog.close());
    const clear = button('全解除', 'btn btn-secondary');
    const apply = button('決定', 'btn btn-primary');
    footer.append(cancel, clear, apply);

    dialog.append(header, body, footer);
    document.body.append(dialog);

    filter.addEventListener('input', () => {
        const needle = filter.value.trim().normalize('NFKC').toLowerCase();
        let visible = 0;
        for (const row of Array.from(rows.rows)) {
            const hidden = !!needle && !(row.dataset.text || '').includes(needle);
            row.hidden = hidden;
            if (!hidden) visible++;
        }
        empty.hidden = visible > 0;
    });

    return { dialog, title, filter, body: rows, head, empty, clear, apply };
}

function selectedTexts(select: HTMLSelectElement): string[] {
    return Array.from(select.selectedOptions)
        .filter(option => option.value)
        .map(option => (option.textContent || '').trim());
}

function label(select: HTMLSelectElement): string {
    const texts = selectedTexts(select);
    if (texts.length === 1) return texts[0];
    if (texts.length === 2) return texts.join('、');
    if (texts.length > 2) return `${texts.slice(0, 2).join('、')} 他${texts.length - 2}件`;
    // 何も選ばれていないとき。ラベルのある入力欄は「未選択」、
    // ラベルを持たない絞り込みは何の条件かが分かるよう項目名を出す。
    const labelled = !!select.id && !!document.querySelector(`label[for="${select.id}"]`);
    if (labelled) return '未選択';
    return select.dataset.selectPickerPlaceholder
        || select.getAttribute('placeholder')
        || '未選択';
}

function title(select: HTMLSelectElement): string {
    return select.dataset.selectPickerTitle
        || select.getAttribute('placeholder')
        || document.querySelector(`label[for="${select.id}"]`)?.textContent?.trim()
        || '一覧から選択';
}

function open(select: HTMLSelectElement): void {
    const parts = (window.__ncSelectPicker ??= build());
    const multiple = select.multiple;

    const pickerTitle = title(select);
    parts.title.textContent = pickerTitle;
    parts.filter.value = '';
    parts.empty.hidden = true;
    // コード付きの選択肢 (銘柄・仮車番など) はコードと名称を別の列に分ける。
    const codeColumn = select.dataset.selectPickerCodeColumn;
    parts.head.innerHTML = codeColumn
        ? `<tr><th class="nc-col-shrink">${multiple ? '選択中' : '選択'}</th><th class="text-nowrap">${codeColumn}</th><th>名称</th></tr>`
        : `<tr><th class="nc-col-shrink">${multiple ? '選択中' : '選択'}</th><th>名称</th></tr>`;
    parts.body.replaceChildren();
    parts.clear.hidden = !multiple;
    parts.apply.hidden = !multiple;

    const checkboxes = new Map<HTMLInputElement, HTMLOptionElement>();
    // 必須の単一選択では、未選択のプレースホルダー (「選択してください」) を候補に出さない。
    // 任意選択では空の選択肢自体が「指定なし」の意味を持つので残す。
    const options = Array.from(select.options)
        .filter(option => !(!select.multiple && select.required && !option.value));

    for (const option of options) {
        const row = parts.body.insertRow();
        const text = (option.textContent || '').trim();
        row.dataset.text = text.normalize('NFKC').toLowerCase();
        const action = row.insertCell();
        action.className = 'text-center text-nowrap nc-col-shrink';
        const code = option.dataset.code || '';
        if (codeColumn) {
            const codeCell = row.insertCell();
            codeCell.className = 'text-nowrap';
            if (code) {
                const mark = document.createElement('code');
                mark.textContent = code;
                codeCell.append(mark);
            }
        }
        const name = row.insertCell();
        // コード列へ出した分は名称から外す。絞り込みは元の文字列のまま効かせる。
        name.textContent = (codeColumn && code && text.startsWith(code + ' ') ? text.slice(code.length + 1) : text) || '未選択';

        if (multiple) {
            const check = document.createElement('input');
            check.type = 'checkbox';
            check.className = 'form-check-input';
            check.checked = option.selected;
            check.setAttribute('aria-label', `${text} を選択`);
            action.append(check);
            checkboxes.set(check, option);
            row.classList.toggle('table-primary', check.checked);
            const toggle = () => {
                row.classList.toggle('table-primary', check.checked);
            };
            check.addEventListener('change', toggle);
            name.style.cursor = 'pointer';
            name.addEventListener('click', () => { check.checked = !check.checked; toggle(); });
        } else {
            if (option.value === select.value) row.className = 'table-primary';
            const pick = document.createElement('button');
            pick.type = 'button';
            pick.className = 'btn btn-sm btn-primary';
            pick.textContent = '選択';
            pick.addEventListener('click', () => {
                select.value = option.value;
                notify(select);
                parts.dialog.close();
            });
            action.append(pick);
        }
    }

    if (multiple) {
        parts.clear.onclick = () => {
            checkboxes.forEach((_option, check) => {
                check.checked = false;
                check.closest('tr')?.classList.remove('table-primary');
            });
        };
        parts.apply.onclick = () => {
            checkboxes.forEach((option, check) => { option.selected = check.checked; });
            notify(select);
            parts.dialog.close();
        };
    }

    parts.dialog.showModal();
    parts.filter.focus({ preventScroll: true });
}

function notify(select: HTMLSelectElement): void {
    select.dispatchEvent(new Event('input', { bubbles: true }));
    select.dispatchEvent(new Event('change', { bubbles: true }));
}

/** セレクトを一覧選択ボタンに差し替える。二重適用は無視する。 */
export function attachSelectPicker(select: HTMLSelectElement): void {
    if (select.dataset.selectPickerReady === 'true') return;
    select.dataset.selectPickerReady = 'true';

    const button = document.createElement('button');
    button.type = 'button';
    const width = select.classList.contains('w-auto') ? 'w-auto' : 'w-100';
    button.className = `btn btn-outline-dark ${width} text-start nc-select-picker-button`;
    if (select.id) button.id = `${select.id}-picker`;
    const sync = () => {
        button.textContent = label(select);
        button.disabled = select.disabled;
        button.classList.toggle('text-body-secondary', !selectedTexts(select).length);
    };
    sync();

    button.addEventListener('click', () => { if (!select.disabled) open(select); });
    select.addEventListener('change', sync);
    // ページ側が値や選択肢を書き換えたときも表示を合わせる。
    select.addEventListener('nc-select-picker-sync', sync);
    new MutationObserver(sync).observe(select, { attributes: true, attributeFilter: ['disabled'] });

    // hidden にすると必須検証でブラウザがフォーカスできず送信が止まるため、
    // 視覚的に隠すだけにして検証・送信は従来どおり動くようにする。
    select.classList.add('visually-hidden');
    select.tabIndex = -1;
    select.setAttribute('aria-hidden', 'true');
    select.after(button);
}

/** 一覧選択ボタンの表示を、いまの <select> の状態に合わせ直す。 */
export function refreshSelectPicker(select: HTMLSelectElement): void {
    select.dispatchEvent(new Event('nc-select-picker-sync'));
}

export function initializeSelectPickers(root: ParentNode = document): void {
    root.querySelectorAll<HTMLSelectElement>('select[data-select-picker]').forEach(attachSelectPicker);
}

document.addEventListener('DOMContentLoaded', () => {
    initializeSelectPickers();
    // モーダルに後から読み込まれたフォームのセレクトにも適用する。
    new MutationObserver(records => {
        for (const record of records) {
            record.addedNodes.forEach(node => {
                if (!(node instanceof Element)) return;
                if (node.matches('select[data-select-picker]')) attachSelectPicker(node as HTMLSelectElement);
                initializeSelectPickers(node);
            });
        }
    }).observe(document.body, { childList: true, subtree: true });
});
